/**
 * "My progress" — kids' achievements view (gamification-core).
 *
 * UNIFIES the existing stickers under the agnostic kit: level, XP as a fun
 * bar, friendly play streak and a grid of MEDALS (milestone badges)
 * that are DERIVED from the same stickers and played days (pure projection over the
 * local `Progress` — it does not duplicate the collection or the reward animation).
 *
 * Design for ages 2-6: big, colorful, without depending on text (emojis). The
 * animations are under `motion-safe` (respects prefers-reduced-motion).
 */

import { createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { createRoute, Link } from "@tanstack/solid-router";
import { ArrowLeft } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import {
  createProgressStore,
  deriveGameState,
  levelProgress,
  melodimalsGamificationConfig,
  PLAY_STREAK_LINE,
  type Progress,
} from "@melodimals/game-core";
import { hidePassiveBanner, showPassiveBanner } from "@melodimals/monetization";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";

/** Icon (emoji) and i18n label per badge — without depending on the child reading. */
const BADGE_META: Record<string, { icon: string; label: MessageDescriptor }> = {
  "first-game": { icon: "🎮", label: msg`First game` },
  "all-games": { icon: "🏆", label: msg`All games` },
  "first-song": { icon: "🎵", label: msg`First song` },
  "music-lover": { icon: "🎶", label: msg`Music lover` },
  "play-streak-3": { icon: "🔥", label: msg`Three days playing` },
  collector: { icon: "⭐", label: msg`Collector` },
  "super-collector": { icon: "🌟", label: msg`Super collector` },
};

const EMPTY_PROGRESS: Progress = { bestLevel: {}, songsCompleted: [], stickers: [], playDays: [] };

function AchievementsPage() {
  const { t } = useI18n();
  const [progress, setProgress] = createSignal<Progress>(EMPTY_PROGRESS);

  onMount(() => {
    setProgress(createProgressStore().load());
  });

  /**
   * The app's only banner surface (placement `progress-banner`): it is a
   * PASSIVE screen, outside the play area, which also already has its own
   * internal scroll zone. The AdMob banner is a NATIVE view anchored
   * over the WebView, so it does not push the layout nor can it cause the scroll
   * that the product forbids. Outside Android all of this is a no-op.
   */
  onMount(() => {
    void showPassiveBanner();
  });
  onCleanup(() => {
    void hidePassiveBanner();
  });

  const state = createMemo(() => deriveGameState(progress()));
  const progressBar = createMemo(() => levelProgress(state().xp));
  const streak = createMemo(() => state().streaks[PLAY_STREAK_LINE]?.current ?? 0);
  const earned = createMemo(() => new Set(state().badges));

  return (
    <main class="kid-stage-screen screen-enter bg-background p-4">
      <header class="flex items-center gap-3 pb-3">
        <Link
          to="/collection"
          class="tile-3d tile-3d-press flex h-14 w-14 items-center justify-center rounded-full bg-surface"
          aria-label={t(msg`Back`)}
        >
          <ArrowLeft class="h-7 w-7 text-foreground" />
        </Link>
        <span class="text-4xl" aria-hidden="true">
          🏅
        </span>
      </header>

      <div class="min-h-0 flex-1 overflow-y-auto" data-testid="achievements-page">
        {/* Nivel + barra de XP: la recompensa de "cuánto he jugado" en grande. */}
        <section class="mb-4 rounded-3xl bg-surface p-4 shadow-sm">
          <div class="flex items-center gap-4">
            <div
              class="flex h-20 w-20 shrink-0 items-center justify-center rounded-full bg-gradient-to-b from-joy-sunshine to-joy-tangerine text-4xl font-black text-amber-900 shadow-md ring-4 ring-white/70"
              data-testid="player-level"
            >
              {state().level}
            </div>
            <div class="min-w-0 flex-1">
              <div class="mb-2 flex items-center justify-between text-sm font-semibold text-muted">
                <span>{t(msg`My level`)}</span>
                <span class="tabular-nums text-foreground" data-testid="xp-total">
                  {state().xp} {t(msg`XP`)}
                </span>
              </div>
              <div
                class="h-5 w-full overflow-hidden rounded-full bg-surface-secondary"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progressBar().ratio * 100)}
                aria-label={t(msg`Level progress`)}
              >
                <div
                  class="h-full w-full origin-left rounded-full bg-gradient-to-r from-joy-grape to-joy-bubblegum motion-safe:transition-transform motion-safe:duration-500"
                  style={{ transform: `scaleX(${progressBar().ratio})` }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Racha de juego amable — cuántos días seguidos ha jugado. */}
        <section class="mb-4 flex items-center gap-4 rounded-3xl bg-surface p-4 shadow-sm">
          <span class="text-4xl" aria-hidden="true">
            🔥
          </span>
          <div>
            <div
              class="text-3xl font-black tabular-nums text-foreground"
              data-testid="streak-current"
            >
              {streak()}
            </div>
            <div class="text-sm font-semibold text-muted">{t(msg`Days in a row`)}</div>
          </div>
        </section>

        {/* Medallas (badges) — se DERIVAN de los stickers y días jugados. */}
        <section
          data-testid="badge-grid"
          class="grid grid-cols-3 gap-4 pb-4 sm:grid-cols-4 lg:grid-cols-5"
        >
          <For each={melodimalsGamificationConfig.badges ?? []}>
            {(badge) => {
              const isEarned = () => earned().has(badge.id);
              const meta = BADGE_META[badge.id];
              return (
                <div
                  data-testid={`badge-${badge.id}`}
                  data-earned={isEarned() ? "true" : "false"}
                  class="flex flex-col items-center gap-1"
                >
                  <div
                    class={
                      isEarned()
                        ? "flex aspect-square w-full items-center justify-center rounded-3xl bg-gradient-to-b from-joy-sunshine to-joy-tangerine text-5xl shadow-md ring-4 ring-white/70"
                        : "flex aspect-square w-full items-center justify-center rounded-3xl border-2 border-dashed border-muted/40 bg-surface-secondary text-5xl opacity-50 grayscale"
                    }
                  >
                    <span aria-hidden="true">{meta?.icon ?? "⭐"}</span>
                  </div>
                  <Show when={meta}>
                    {(m) => (
                      <span class="text-center text-xs font-semibold text-muted">
                        {t(m().label)}
                      </span>
                    )}
                  </Show>
                </div>
              );
            }}
          </For>
        </section>
      </div>
    </main>
  );
}

export const progressRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/progress",
  component: AchievementsPage,
});
